import { useState } from "react";
import { X, Calendar, Stethoscope, CheckCircle } from "lucide-react";
import PropTypes from "prop-types";
import RescheduleModal from "./RescheduleModal.js";
import CancelModal from "./CancelModal.js";
import "../css/scheduledProceduresModal.css";
import courtesy from "../../../assets/img/courtesy-selected.png";
import formatDate from "../../../utils/formatDate.js";

const formatCurrency = (value) => {
  return "R$ " + parseFloat(value || 0).toFixed(2).replace('.', ',');
};

const ScheduledProceduresModal = ({ patient, onClose, onRemove, onReschedule }) => {
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  const schedules = patient.schedules || [];
  const hasAnyProcedures = schedules.length > 0;

  const handleReschedule = (schedule) => {
    setSelectedSchedule(schedule);
    setShowRescheduleModal(true);
  };

  const handleCancel = (schedule) => {
    setSelectedSchedule(schedule);
    setShowCancelModal(true);
  };

  const removeSchedule = (schedules, schedulesIds) => {
    if (onRemove) {
      onRemove(schedules, schedulesIds);
    }
  };

  return (
    <>
      <div className="modal-overlay">
        <div className="procedures-modal scheduled-procedures-modal">
          <div className="modal-header">
            <h2>Procedimentos Agendados - {patient.name}</h2>
            <button className="close-button" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-content-scrollable">
            <div className="scheduled-procedures-list">
              {hasAnyProcedures ? (
                schedules.map((schedule) => {
                  const { id, scheduling_date, items } = schedule;

                  const dateFormatted = new Date(scheduling_date).toLocaleDateString("pt-BR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });

                  const totalValue = items
                    .filter((item) => item.agreement === null)
                    .reduce(
                      (sum, item) => sum + parseFloat(item.selling_price || 0),
                      0
                    );

                  const allItemsFinished = items.every((i) => i.cancel || i.confirmed);

                  return (
                    <div key={id} className="scheduled-procedure-card">
                      <div className="scheduled-procedure-header">
                        <div className="scheduled-date-time">
                          <div className="scheduled-date">
                            <Calendar size={16} />
                            <span>{dateFormatted}</span>
                          </div>
                        </div>
                      </div>

                      <ul className="scheduled-procedures">
                        {items.map((item, idx) => (
                          <li key={idx}>
                            <div className="scheduled-doctor">
                              <Stethoscope size={16} />
                              <span>
                                {item.professional_name ||
                                  "Profissional não informado"}
                              </span>
                            </div>
                            <strong>Procedimento:</strong>
                            <div className="scheduled-info-row">
                              <span
                                className="limit_caracters"
                                title={item.procedure_name}>
                                {item.procedure_name}
                              </span>
                              <span>
                                {item.courtesy && <img src={courtesy} width={50} alt="Cortesia" />}
                              </span>
                              <span>{formatDate(item.date_scheduling)}</span>
                              <span>{item.time_booked || "Sem horário"}</span>
                              <span className="procedure-price">
                                {item.agreement !== null ? item.agreement : formatCurrency(item.selling_price)}
                              </span>

                              <div style={{ cursor: 'pointer '}} title={item.confirmed ? "Exame realizado" : item.cancel ? "Exame cancelado" : "Exame agendado"}>
                                <CheckCircle
                                  size={20}
                                  className={`status-icon status-${item.confirmed ? "confirmed" : item.cancel ? "cancelled" : "scheduled"}`}
                                />
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>

                      <div className="scheduled-total">
                        <span>Valor Total:</span>
                        <span className="total-price">
                          {formatCurrency(totalValue)}
                        </span>
                      </div>

                      {
                        !allItemsFinished && (
                          <div className="scheduled-procedure-actions">
                            <button
                              className="reschedule-button"
                              onClick={() =>
                                handleReschedule({
                                  procedures: items,
                                  date: scheduling_date,
                                })
                              }
                            >
                              Reagendar
                            </button>

                            <button
                              className="cancel-appointment-button"
                              onClick={() =>
                                handleCancel({
                                  procedures: items,
                                  date: scheduling_date,
                                })
                              }
                            >
                              Cancelar
                            </button>
                          </div>
                        )
                      }

                    </div>
                  );
                })
              ) : (
                <div className="no-scheduled-procedures">
                  <Calendar size={48} />
                  <p>Nenhum procedimento agendado para este paciente.</p>
                  <button className="schedule-new-button" onClick={onClose}>
                    Agendar Novo Procedimento
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button className="cancel-button" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>

      {showRescheduleModal && selectedSchedule && (
        <RescheduleModal
          schedule={selectedSchedule}
          patient={patient}
          onClose={() => setShowRescheduleModal(false)}
          onConfirm={(updated) => {
            onReschedule(updated);
            setShowRescheduleModal(false);
          }}
        />
      )}

      {showCancelModal && selectedSchedule && (
        <CancelModal
          schedule={selectedSchedule}
          patient={patient}
          onClose={() => setShowCancelModal(false)}
          onConfirm={(payload) => {
            handleCancel(payload);
            removeSchedule(
              payload.canceledProcedures,
              payload.canceledSchedules
            );
          }}
        />
      )}
    </>
  );
};

ScheduledProceduresModal.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    schedules: PropTypes.array, // novo campo
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
};

export default ScheduledProceduresModal;
